import { universalComposioInstaller } from '@/apis/universalComposioInstaller';
import { useToast } from '@/hooks/useToast';
import { useUserLogged } from '@/hooks/useUserLogged';
import { useMutation } from '@tanstack/react-query';
import { getFrontUrl } from '@/lib/auth/frontUrl';
import { isOwner } from '@/lib/rbac';

interface ComposioInstallParams {
  integrationName: string;
  sentryAccessToken?: string;
  /** GCP Cloud Logging: raw service-account JSON key text. */
  gcpServiceAccountJson?: string;
  /** GCP Cloud Logging: project ID. */
  gcpProjectId?: string;
  /** GCP Cloud Logging: auth mode — 'sa_key' (default) or 'impersonation'. */
  gcpAuthMode?: string;
  /** GCP Cloud Logging: target SA email for impersonation mode. */
  gcpTargetServiceAccountEmail?: string;
  datadogApiKey?: string;
  /** Datadog site/region (e.g. us1, eu, us3, us5). */
  datadogRegion?: string;
  /** Datadog Application Key / generic_id. Optional. */
  datadogGenericId?: string;
  redirectUrl?: string;
  cloudId?: string; // For Jira cloud ID
  subdomain?: string; // For Jira subdomain (e.g., "yourcompany" for "yourcompany.atlassian.net")
  mixpanelServiceAccountSecret?: string;
  mixpanelServiceAccountUsername?: string;
  mixpanelRegion?: string;
  firefliesApiKey?: string;
  /** Granola Enterprise API key (Composio API_KEY auth) */
  granolaApiKey?: string;
  /** Lightfield API key (custom Composio API_KEY auth config) */
  lightfieldApiKey?: string;
  newRelicApiKey?: string;
  newRelicRegion?: string;
  betterStackApiKey?: string;
  /** Optional Query API password for Better Stack log queries (Ellie) */
  betterStackQueryApiPassword?: string;
  /** PagerDuty API key (Composio API_KEY auth); omit for OAuth. */
  pagerDutyApiKey?: string;
  /** Incident.io API key (Composio API_KEY auth). */
  incidentIoApiKey?: string;
  /** ELK: full Elasticsearch cluster URL */
  elkBaseUrl?: string;
  /** ELK: Elasticsearch API key for authentication */
  elkApiKey?: string;
  /** ELK: comma-separated index patterns to query */
  elkIndexPattern?: string;
  /** Grafana service account token (Bearer Token auth). */
  grafanaApiKey?: string;
  /** Grafana instance base URL, e.g. https://yourorg.grafana.net */
  grafanaBaseUrl?: string;
  /** Grafana bearer token for bearer authentication. */
  grafanaBearerToken?: string;
  /** AWS CloudWatch: IAM access key ID */
  awsAccessKeyId?: string;
  /** AWS CloudWatch: IAM secret access key */
  awsSecretAccessKey?: string;
  /** AWS CloudWatch: AWS region (e.g., us-east-1) */
  awsRegion?: string;
  /** Azure Monitor: Azure AD tenant ID */
  azureTenantId?: string;
  /** Azure Monitor: Azure AD app client ID */
  azureClientId?: string;
  /** Azure Monitor: Azure AD app client secret */
  azureClientSecret?: string;
  /** Azure Monitor: Azure subscription ID */
  azureSubscriptionId?: string;
  /** Axiom API token (direct integration; stored as ApiKey on backend) */
  axiomApiKey?: string;
  /** Optional org id for PAT (`x-axiom-org-id`) */
  axiomOrgId?: string;
  /** Optional API base URL (default https://api.axiom.co) */
  axiomApiHost?: string;
  /** Sumo Logic: Access ID from Administration > Security > Access Keys */
  sumologicAccessId?: string;
  /** Sumo Logic: Access Key from Administration > Security > Access Keys */
  sumologicAccessKey?: string;
  /** Sumo Logic: deployment region (e.g. us1, us2, eu, au, ca, in, jp, fed) */
  sumologicDeployment?: string;
}

export const useComposioInstaller = () => {
  const toast = useToast();
  const { user, organization, status } = useUserLogged();

  const initiateInstall = async (params: ComposioInstallParams) => {
    // Match org resolution used across the app (e.g. useEKUSourceMapping):
    // org context can lag behind user; User.OrganizationUUID is still valid.
    const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';
    if (!user?.UserUUID || !orgUuid) {
      throw new Error('User or organization data not available');
    }

    const response = await universalComposioInstaller({
      IntegrationName: params.integrationName,
      UserUUID: user.UserUUID,
      OrgUUID: orgUuid,
      SentryAccessToken: params.sentryAccessToken,
      GcpServiceAccountJson: params.gcpServiceAccountJson,
      GcpProjectId: params.gcpProjectId,
      GcpAuthMode: params.gcpAuthMode,
      GcpTargetServiceAccountEmail: params.gcpTargetServiceAccountEmail,
      DatadogApiKey: params.datadogApiKey,
      DatadogRegion: params.datadogRegion,
      DatadogGenericId: params.datadogGenericId,
      RedirectUrl: params.redirectUrl
        ? `${getFrontUrl()}${params.redirectUrl}`
        : undefined,
      CloudId: params.cloudId,
      Subdomain: params.subdomain,
      MixpanelServiceAccountSecret: params.mixpanelServiceAccountSecret,
      MixpanelServiceAccountUsername: params.mixpanelServiceAccountUsername,
      MixpanelRegion: params.mixpanelRegion,
      FirefliesApiKey: params.firefliesApiKey,
      GranolaApiKey: params.granolaApiKey,
      ApiKey:
        params.firefliesApiKey ||
        params.newRelicApiKey ||
        params.betterStackApiKey ||
        params.granolaApiKey ||
        params.axiomApiKey ||
        params.lightfieldApiKey,
      NewRelicRegion: params.newRelicRegion,
      BetterStackQueryApiPassword: params.betterStackQueryApiPassword,
      PagerDutyApiKey: params.pagerDutyApiKey,
      IncidentIoApiKey: params.incidentIoApiKey,
      ElkBaseUrl: params.elkBaseUrl,
      ElkApiKey: params.elkApiKey,
      ElkIndexPattern: params.elkIndexPattern,
      GrafanaApiKey: params.grafanaApiKey,
      GrafanaBaseUrl: params.grafanaBaseUrl,
      GrafanaBearerToken: params.grafanaBearerToken,
      AwsAccessKeyId: params.awsAccessKeyId,
      AwsSecretAccessKey: params.awsSecretAccessKey,
      AwsRegion: params.awsRegion,
      AzureTenantId: params.azureTenantId,
      AzureClientId: params.azureClientId,
      AzureClientSecret: params.azureClientSecret,
      AzureSubscriptionId: params.azureSubscriptionId,
      AxiomOrgId: params.axiomOrgId,
      AxiomApiHost: params.axiomApiHost,
      SumologicAccessId: params.sumologicAccessId,
      SumologicAccessKey: params.sumologicAccessKey,
      SumologicDeployment: params.sumologicDeployment,
    });

    // Notify the integrations page to refresh its connection state. Fires for
    // both `initiateInstall` callers (e.g. LightfieldApiKeyModal,
    // CloudWatchApiKeyModal) and `handleCommonInstall` callers (which route
    // through `mutationFn` -> `initiateInstall`). Skip when there's a redirect
    // (page navigates) or a server error (nothing actually connected).
    if (
      typeof window !== 'undefined' &&
      response &&
      !response.Error &&
      !response.RedirectUrl
    ) {
      window.dispatchEvent(new Event('integration-connected'));
    }

    return response;
  };

  const mutation = useMutation({
    mutationFn: async (params: ComposioInstallParams) => {
      return await initiateInstall(params);
    },
    onSuccess: (response, variables) => {
      if (response && response?.Error) {
        toast(
          response.Forbidden
            ? {
                title: 'Access Denied',
                description: 'Only Owners can connect organization integrations.',
                status: 'error',
                duration: 5000,
                isClosable: true,
              }
            : {
                title: 'Error',
                description: response.Error || 'Failed to initiate connection',
                status: 'error',
                duration: 5000,
                isClosable: true,
              }
        );
      } else {
        if (response.RedirectUrl) {
          window.location.href = response.RedirectUrl;
        } else {
          toast({
            title: 'Connected',
            description: `${variables.integrationName} connected successfully.`,
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          // Note: `initiateInstall` already dispatched
          // `integration-connected` for the success-without-redirect case.
        }
      }
    },
    onError: (error) => {
      console.error('Composio installation failed:', error);
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to initiate connection';
      toast({
        title: 'Error',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleCommonInstall = (
    integrationName: string,
    sentryAccessToken?: string,
    datadogApiKey?: string,
    redirectUrl?: string,
    cloudId?: string,
    subdomain?: string,
    mixpanelServiceAccountSecret?: string,
    mixpanelServiceAccountUsername?: string,
    mixpanelRegion?: string,
    firefliesApiKey?: string,
    newRelicApiKey?: string,
    newRelicRegion?: string,
    betterStackApiKey?: string,
    betterStackQueryApiPassword?: string,
    datadogRegion?: string,
    datadogGenericId?: string,
    pagerDutyApiKey?: string,
    incidentIoApiKey?: string,
    granolaApiKey?: string,
    elkBaseUrl?: string,
    elkApiKey?: string,
    elkIndexPattern?: string,
    grafanaApiKey?: string,
    grafanaBaseUrl?: string,
    grafanaBearerToken?: string,
    awsAccessKeyId?: string,
    awsSecretAccessKey?: string,
    awsRegion?: string,
    azureTenantId?: string,
    azureClientId?: string,
    azureClientSecret?: string,
    azureSubscriptionId?: string,
    gcpServiceAccountJson?: string,
    gcpProjectId?: string
  ) => {
    // Every connector's connect endpoint (`initiateConnection/`,
    // `saveGroupAccessToken`-style) is Owner-only on the backend
    // (`require_owner_scope`). Guard once here so no caller can fire it for
    // a non-org-wide viewer, rather than gating each of the several UI entry
    // points (Settings, Home, onboarding, incident-knowledge) separately.
    if (!(status === 'authenticated' && isOwner(user?.Role))) {
      toast({
        title: 'Access Denied',
        description: 'Only Owners can manage organization integrations.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    mutation.mutate({
      integrationName,
      sentryAccessToken,
      datadogApiKey,
      redirectUrl,
      cloudId,
      subdomain,
      mixpanelServiceAccountSecret,
      mixpanelServiceAccountUsername,
      mixpanelRegion,
      firefliesApiKey,
      newRelicApiKey,
      newRelicRegion,
      betterStackApiKey,
      betterStackQueryApiPassword,
      datadogRegion,
      datadogGenericId,
      pagerDutyApiKey,
      incidentIoApiKey,
      granolaApiKey,
      elkBaseUrl,
      elkApiKey,
      elkIndexPattern,
      grafanaApiKey,
      grafanaBaseUrl,
      grafanaBearerToken,
      awsAccessKeyId,
      awsSecretAccessKey,
      awsRegion,
      azureTenantId,
      azureClientId,
      azureClientSecret,
      azureSubscriptionId,
      gcpServiceAccountJson,
      gcpProjectId,
    });
  };

  const orgUuidReady = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';
  const isReady =
    status === 'authenticated' && !!user?.UserUUID && !!orgUuidReady;
  // True once the role is known to be Owner. Also true while the role hasn't
  // loaded yet: onboarding steps render before the user row resolves, and
  // the org creator is always an Owner there; the backend still enforces
  // `require_owner_scope`.
  const canManageIntegrations = isOwner(user?.Role) || !user?.Role;

  return {
    handleCommonInstall,
    initiateInstall,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    isReady,
    canManageIntegrations,
    mutation,
  };
};
